import { ref, computed, reactive } from 'vue'
import { defineStore } from 'pinia'
import {
  ACCOUNT_SIDE,
  LOAN_ENTRY,
  LOAN_STATUS,
  PAY_CYCLE,
  PERIOD_STATUS,
  TRANSACT_SOURCE,
  type Loan,
  type LoanTrans,
  type Payslip,
  type Period,
  type Transact
} from './types'
import { useUtil } from '@/composables/util'
import { useAccountStore } from './account'
import { usePeriodStore } from './period'
import { useTransactStore } from './transact'
import dayjs from 'dayjs'
import axios from 'axios'
import { API } from '@/config'

const apiURL: string = API

const api = axios.create({
  baseURL: apiURL + '/v1'
})

const my = useUtil()

// How many payroll cuts a month has for each cycle. Used to decide which of
// deduction_1..4 a period collects, and how splitAmortization divides.
export const cutsInMonth = (pay_cycle?: string) => {
  if (pay_cycle == PAY_CYCLE.Monthly) return 1
  if (pay_cycle == PAY_CYCLE.Weekly) return 4
  return 2 // Semi-Monthly, and the default elsewhere in the app
}

export const useLoanStore = defineStore('loan', () => {
  const loans = reactive(Array<Loan>())
  const loanTrans = reactive(Array<LoanTrans>())

  const selectedLoan = ref({} as Loan) // selected in table
  const thisLoan = ref({} as Loan) // for form editing

  const showForm = ref(false)

  const accountStore = useAccountStore()
  const periodStore = usePeriodStore()
  const transactStore = useTransactStore()

  // ---------------------------------------------------------------------------
  // persistence
  // ---------------------------------------------------------------------------

  // Strip the audit columns web2py stamps itself. An edited row is a copy of what
  // the API returned, so sending them back freezes modified_on — the same trap
  // toApiPayload guards against in transact.ts.
  function toApiPayload(payload: any) {
    const { created_on, modified_on, created_by, modified_by, ip, ...rest } = payload ?? {}
    return rest
  }

  async function getLoans() {
    const response = await attempt(() => api.get('/loans.json'))
    if (!response || response.status != 200) return
    loans.splice(0)
    Object.assign(loans, response.data.data ?? [])
  }

  async function getLoanTrans() {
    const response = await attempt(() => api.get('/loan_trans.json'))
    if (!response || response.status != 200) return
    loanTrans.splice(0)
    Object.assign(loanTrans, response.data.data ?? [])
  }

  function rememberLoan(row: Loan) {
    const idx = loans.findIndex((item: Loan) => item != undefined && item.uuid == row.uuid)
    if (idx >= 0) loans.splice(idx, 1, row)
    else loans.push(row)
  }

  function rememberLine(row: LoanTrans) {
    const idx = loanTrans.findIndex((item: LoanTrans) => item != undefined && item.uuid == row.uuid)
    if (idx >= 0) loanTrans.splice(idx, 1, row)
    else loanTrans.push(row)
  }

  function forgetLine(uuid: string) {
    const idx = loanTrans.findIndex((item: LoanTrans) => item != undefined && item.uuid == uuid)
    if (idx >= 0) loanTrans.splice(idx, 1)
  }

  /**
   * Swallow a rejected request instead of letting it escape.
   *
   * calcPay awaits postLoanDeductions before it totals the payslip, so a throw
   * from here would abort the calculation and leave total_credit and net_pay
   * unwritten. A ledger write that fails must cost the collection, not the
   * payslip. It matters in practice: the uq_loan_payslip_type constraint answers
   * a duplicate insert with a web2py 500 ticket rather than an error body, and
   * axios rejects on that.
   */
  async function attempt<T>(request: () => Promise<T>) {
    try {
      return await request()
    } catch {
      return undefined
    }
  }

  // A failed insert can also answer 200 with {errors:{...}} in the body rather
  // than the saved row, so the status alone would report a phantom success.
  async function addLine(payload: Partial<LoanTrans>) {
    const response = await attempt(() => api.post('/loan_trans.json', toApiPayload(payload)))
    if (!response || response.status != 200 || !response.data?.uuid) return undefined
    rememberLine(response.data)
    return response.data as LoanTrans
  }

  async function putLine(payload: LoanTrans) {
    const response = await attempt(() =>
      api.put('/loan_trans/' + payload.uuid, toApiPayload(payload))
    )
    // On a matched update the API echoes the stored row; when nothing matched it
    // echoes the payload back, which has no created_on to give it away.
    if (!response || response.status != 200 || !response.data?.created_on) return undefined
    rememberLine(response.data)
    return response.data as LoanTrans
  }

  async function deleteLine(payload: LoanTrans) {
    if (!payload?.uuid) return false
    // The API answers 200 with {deleted: 0} when nothing matched, so trust the
    // row count rather than the status.
    const response = await attempt(() => api.delete('/loan_trans/' + payload.uuid))
    if (!response || response.status != 200 || !Number(response.data?.deleted)) return false
    forgetLine(payload.uuid)
    return true
  }

  // Re-read one loan's lines. Used to recover from a lost race: another session
  // collected this payslip, so our copy of the ledger is behind.
  async function refreshLoanLines(loan_uuid: string) {
    const response = await attempt(() => api.get('/loan_trans/loan-uuid/' + loan_uuid))
    if (!response || response.status != 200) return false
    for (const row of response.data?.data ?? []) rememberLine(row)
    return true
  }

  // ---------------------------------------------------------------------------
  // the ledger
  // ---------------------------------------------------------------------------

  const linesFor = (loan_uuid: string) =>
    loanTrans.filter((item: LoanTrans) => item != undefined && item.loan_uuid == loan_uuid)

  const signOf = (line: LoanTrans) => (line.account_side == ACCOUNT_SIDE.Debit ? 1 : -1)

  /**
   * Outstanding balance: sum(debit) - sum(credit).
   *
   * `exceptPayslipUuid` drops that payslip's own Amortization line. calcPay
   * re-runs every time a payslip is opened, and without the exclusion each run
   * would see its own previous collection as already paid and compute a smaller
   * amount than the run before it.
   */
  function balanceOf(loan_uuid: string, exceptPayslipUuid?: string) {
    return linesFor(loan_uuid)
      .filter((line: LoanTrans) => !exceptPayslipUuid || line.payslip_uuid != exceptPayslipUuid)
      .reduce((total: number, line: LoanTrans) => total + signOf(line) * my.parseMoney(line.amount), 0)
  }

  // The loan's lines oldest first, each carrying the running balance after it —
  // what the ledger panel renders.
  const ledgerFor = computed(() => (loan_uuid: string) => {
    const ordered = linesFor(loan_uuid).slice().sort((a: LoanTrans, b: LoanTrans) => {
      const at = dayjs(a.trans_date ?? (a as any).created_on).valueOf() || 0
      const bt = dayjs(b.trans_date ?? (b as any).created_on).valueOf() || 0
      return at - bt
    })

    let running = 0
    return ordered.map((line: LoanTrans) => {
      const amount = my.parseMoney(line.amount)
      running += signOf(line) * amount
      return {
        ...line,
        debit: line.account_side == ACCOUNT_SIDE.Debit ? amount : 0,
        credit: line.account_side == ACCOUNT_SIDE.Credit ? amount : 0,
        running_balance: running
      }
    })
  })

  const lineFor = (loan_uuid: string, payslip_uuid: string, entry_type: LOAN_ENTRY) =>
    loanTrans.find(
      (item: LoanTrans) =>
        item != undefined &&
        item.loan_uuid == loan_uuid &&
        item.payslip_uuid == payslip_uuid &&
        item.entry_type == entry_type
    )

  const releaseLineFor = (loan_uuid: string) =>
    loanTrans.find(
      (item: LoanTrans) =>
        item != undefined && item.loan_uuid == loan_uuid && item.entry_type == LOAN_ENTRY.Release
    )

  /**
   * Keep the loan's single Release debit line equal to principal_amount. Editing
   * the principal re-points that one line rather than stacking another on top,
   * which is why the loan can be corrected after it has already been collected on.
   */
  async function syncReleaseLine(loan: Loan) {
    const principal = my.parseMoney(loan.principal_amount)
    const existing = releaseLineFor(loan.uuid)

    if (existing) {
      if (my.parseMoney(existing.amount) == principal) return existing
      return await putLine({ ...existing, amount: principal } as LoanTrans)
    }

    return await addLine({
      loan_uuid: loan.uuid,
      entry_type: LOAN_ENTRY.Release,
      account_side: ACCOUNT_SIDE.Debit,
      amount: principal,
      trans_date: my.toApiDateTime(loan.start_date) ?? my.toApiDateTime(dayjs().toDate()),
      employee_uuid: loan.employee_uuid,
      account_uuid: loan.account_uuid,
      remarks: 'Loan release'
    } as any)
  }

  /**
   * A hand-entered correction. Debit raises the balance, Credit reduces it.
   *
   * `trans_date` is when the movement happened, which is not always today: a
   * cash payment being recorded a week late belongs on the date it was received,
   * and the ledger orders its lines by it. Falls back to today when omitted, and
   * when the value cannot be parsed rather than sending a null the column rejects.
   */
  async function postAdjustment(
    loan: Loan,
    account_side: ACCOUNT_SIDE,
    amount: number | string,
    remarks?: string,
    trans_date?: Date | string | null
  ) {
    const value = my.parseMoney(amount)
    if (!Number.isFinite(value) || value <= 0) return undefined

    const when = my.toApiDateTime(trans_date) ?? my.toApiDateTime(dayjs().toDate())

    const line = await addLine({
      loan_uuid: loan.uuid,
      entry_type: LOAN_ENTRY.Adjustment,
      account_side,
      amount: value,
      trans_date: when,
      employee_uuid: loan.employee_uuid,
      account_uuid: loan.account_uuid,
      remarks: remarks || 'Adjustment'
    } as any)

    if (line) await refreshBalance(loan)
    return line
  }

  // Change an existing Adjustment's date or amount. Only Adjustments: Release is
  // owned by the loan's principal_amount and Amortization by payroll.
  async function editAdjustment(
    line: LoanTrans,
    changes: { amount?: number | string; trans_date?: Date | string | null; remarks?: string }
  ) {
    if (!line?.uuid || line.entry_type != LOAN_ENTRY.Adjustment) return undefined

    const next = { ...line } as LoanTrans

    if (changes.amount !== undefined) {
      const value = my.parseMoney(changes.amount)
      if (!Number.isFinite(value) || value <= 0) return undefined
      next.amount = value
    }
    if (changes.trans_date !== undefined) {
      const when = my.toApiDateTime(changes.trans_date)
      if (!when) return undefined
      next.trans_date = when as any
    }
    if (changes.remarks !== undefined) next.remarks = changes.remarks

    const saved = await putLine(next)
    if (saved) {
      const loan = loans.find((item: Loan) => item.uuid == line.loan_uuid)
      if (loan) await refreshBalance(loan)
    }
    return saved
  }

  // ---------------------------------------------------------------------------
  // amortization schedule
  // ---------------------------------------------------------------------------

  /**
   * Which cut of the month a period is, 1-based.
   *
   * Derived from the periods already loaded app-wide: the periods of the same
   * pay cycle in the same calendar month, in date order. Falls back to the day of
   * the month when the period is not in that list (a payslip opened for a period
   * outside the loaded year, say).
   */
  function cutIndex(period: Period) {
    if (!period?.start_date) return 1

    const start = dayjs(period.start_date)
    const siblings = periodStore.periods
      .filter((item: Period) => item != undefined && item.pay_cycle == period.pay_cycle)
      .filter((item: Period) => dayjs(item.start_date).isSame(start, 'month'))
      .slice()
      .sort((a: Period, b: Period) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf())

    const idx = siblings.findIndex((item: Period) => item.uuid == period.uuid)
    if (idx >= 0) return idx + 1

    const day = start.date()
    if (period.pay_cycle == PAY_CYCLE.Monthly) return 1
    if (period.pay_cycle == PAY_CYCLE.Weekly) return Math.min(4, Math.ceil(day / 7))
    return day <= 15 ? 1 : 2
  }

  // Default fill for deduction_1..4 when a loan is created. The user may then
  // move the whole amount onto one cut by hand.
  function splitAmortization(monthly: number | string, pay_cycle?: string) {
    const amount = my.parseMoney(monthly)
    const cuts = cutsInMonth(pay_cycle)
    const share = cuts > 0 ? Math.round((amount / cuts) * 100) / 100 : 0

    const split = [0, 0, 0, 0]
    for (let i = 0; i < cuts; i++) split[i] = share
    // put any rounding remainder on the first cut so the cuts sum to the monthly
    if (cuts > 0) split[0] = Math.round((amount - share * (cuts - 1)) * 100) / 100
    return split
  }

  const scheduledFor = (loan: Loan, cut: number) => {
    const value = (loan as any)['deduction_' + cut]
    return my.parseMoney(value ?? 0)
  }

  // ---------------------------------------------------------------------------
  // loan master
  // ---------------------------------------------------------------------------

  const employeeLoans = computed(() => (employee_uuid: string) =>
    loans.filter((item: Loan) => item != undefined && item.employee_uuid == employee_uuid)
  )

  const activeLoans = computed(() => (employee_uuid: string) =>
    employeeLoans.value(employee_uuid).filter((item: Loan) => item.status == LOAN_STATUS.Active)
  )

  // Write the ledger total back onto the loan's cached balance, and retire the
  // loan once it is settled. A Paid loan that is later adjusted upward goes back
  // to Active, so a correction does not need the status changed by hand too.
  async function refreshBalance(loan: Loan) {
    const balance = Math.round(balanceOf(loan.uuid) * 100) / 100
    let status = loan.status

    if (status == LOAN_STATUS.Active && balance <= 0) status = LOAN_STATUS.Paid
    else if (status == LOAN_STATUS.Paid && balance > 0) status = LOAN_STATUS.Active

    if (my.parseMoney(loan.balance) == balance && status == loan.status) return loan

    const response = await attempt(() =>
      api.put('/loans/' + loan.uuid, toApiPayload({ ...loan, balance, status }))
    )
    if (!response || response.status != 200 || !response.data?.created_on) return loan

    rememberLoan(response.data)
    return response.data as Loan
  }

  async function saveLoan(payload: Loan) {
    // The datepicker hands back a Date. JSON.stringify would send UTC ISO with a
    // Z, shifting PH times back 8 hours and sometimes filing the loan under the
    // previous day, so format it the way the API stores it. The PUT handler does
    // no date coercion of its own, unlike POST.
    const body = toApiPayload({
      ...payload,
      start_date: my.toApiDateTime(payload.start_date) ?? payload.start_date
    })

    let saved: Loan | undefined
    if (payload.uuid) {
      const response = await api.put('/loans/' + payload.uuid, body)
      if (response.status != 200 || !response.data?.created_on) return undefined
      saved = response.data as Loan
    } else {
      const response = await api.post('/loans.json', body)
      if (response.status != 200 || !response.data?.uuid) return undefined
      saved = response.data as Loan
    }

    rememberLoan(saved)
    await syncReleaseLine(saved)
    saved = await refreshBalance(saved)

    // the employee's Open payslips no longer match the loan they were calculated from
    await dirtyLoanPayslips(saved)
    return saved
  }

  async function deleteLoan(payload: Loan) {
    if (!payload?.uuid) return false

    // Drop the ledger first: a loan whose lines outlived it would leave orphan
    // rows that no screen can reach.
    for (const line of linesFor(payload.uuid).slice()) await deleteLine(line)

    const response = await api.delete('/loans/' + payload.uuid)
    if (response.status != 200 || !Number(response.data?.deleted)) return false

    const idx = loans.findIndex((item: Loan) => item.uuid == payload.uuid)
    if (idx >= 0) loans.splice(idx, 1)
    if (selectedLoan.value?.uuid == payload.uuid) selectedLoan.value = {} as Loan

    await dirtyLoanPayslips(payload)
    return true
  }

  // Imported lazily: payslip.ts imports this store, so a top-level import here
  // would close the cycle at module load. dailytime.ts reaches payslip the same way.
  async function dirtyLoanPayslips(loan: Loan) {
    if (!loan?.employee_uuid) return
    const { usePayslipStore } = await import('./payslip')
    await usePayslipStore().dirtyAffectedPayslips({ employee_uuid: loan.employee_uuid }, false)
  }

  // ---------------------------------------------------------------------------
  // payroll collection
  // ---------------------------------------------------------------------------

  /**
   * Post this payslip's loan collections.
   *
   * Called from calcPay, which re-runs on every payslip open, so every step here
   * is an upsert keyed on (loan, payslip): running it twice must leave the same
   * amount on the payslip and the same single Amortization line in the ledger.
   */
  async function postLoanDeductions(payslip: Payslip, period: Period) {
    // Closed periods are frozen — the same guard calcPay and recalcPeriodPayslips use
    if (!payslip?.uuid || !period?.uuid || period.status != PERIOD_STATUS.Open) return

    const cut = cutIndex(period)
    const due = [] as { loan: Loan; amount: number }[]

    for (const loan of activeLoans.value(payslip.employee_uuid)) {
      const scheduled = scheduledFor(loan, cut)
      // the balance ignoring this payslip's own line, so the final cut collects
      // only the remainder and the loan can never be over-collected
      const outstanding = Math.max(0, balanceOf(loan.uuid, payslip.uuid))
      due.push({ loan, amount: Math.max(0, Math.min(scheduled, outstanding)) })
    }

    // Several loans may point at the same deduction account; the payslip carries
    // one row per account, so they combine there and stay apart in the ledger.
    const byAccount = new Map<string, number>()
    for (const item of due) {
      const account_uuid = item.loan.account_uuid
      if (!account_uuid) continue
      byAccount.set(account_uuid, (byAccount.get(account_uuid) ?? 0) + item.amount)
    }

    const posted = new Set<string>()

    for (const [account_uuid, amount] of byAccount) {
      const row = transactStore.payslipTransactions.find(
        (item: Transact) =>
          item != undefined &&
          item.payslip_uuid == payslip.uuid &&
          item.account_uuid == account_uuid
      )

      if (row) {
        // A clerk's imported or hand-keyed figure wins: updateTransaction refuses
        // to overwrite it. Leave the ledger alone too, so it never claims a
        // collection the payslip does not actually show.
        if (transactStore.isUserProvided(row)) continue
        // attempt(), not a bare await: these helpers reach the network without a
        // guard of their own, and calcPay awaits this whole block before totalling
        // the payslip. updateTransaction resolves to undefined even when it
        // succeeds, so the flag has to come from reaching the end of the closure.
        const updated = await attempt(async () => {
          await transactStore.updateTransaction({ ...row, amount } as Transact)
          return true
        })
        if (!updated) continue
      } else {
        // The account was added to the chart after this payslip was generated, so
        // generateTransactions never seeded a row for it.
        const account = accountStore.accounts.find((item: any) => item.uuid == account_uuid)
        if (!account) continue
        const created = await attempt(() => transactStore.saveTransaction({
          account_uuid,
          employee_uuid: payslip.employee_uuid,
          payslip_uuid: payslip.uuid,
          period_uuid: payslip.period_uuid,
          account_side: account.account_side,
          source: TRANSACT_SOURCE.System,
          amount,
          transact_date: my.toApiDateTime(period.start_date) as any
        } as Transact))
        if (!created) continue
      }

      posted.add(account_uuid)
    }

    for (const item of due) {
      if (!posted.has(item.loan.account_uuid)) continue
      await upsertAmortization(item.loan, payslip, period, item.amount)
      await refreshBalance(item.loan)
    }
  }

  // One Amortization line per (loan, payslip). A zero collection carries no line
  // at all rather than a 0.00 one, so the ledger reads as a list of movements.
  async function upsertAmortization(loan: Loan, payslip: Payslip, period: Period, amount: number) {
    const existing = lineFor(loan.uuid, payslip.uuid, LOAN_ENTRY.Amortization)
    const value = Math.round(amount * 100) / 100

    if (value <= 0) {
      if (existing) await deleteLine(existing)
      return undefined
    }

    if (existing) {
      if (my.parseMoney(existing.amount) == value) return existing
      return await putLine({ ...existing, amount: value } as LoanTrans)
    }

    const added = await addLine({
      loan_uuid: loan.uuid,
      entry_type: LOAN_ENTRY.Amortization,
      account_side: ACCOUNT_SIDE.Credit,
      amount: value,
      trans_date: my.toApiDateTime(period.start_date),
      payslip_uuid: payslip.uuid,
      period_uuid: payslip.period_uuid,
      employee_uuid: loan.employee_uuid,
      account_uuid: loan.account_uuid,
      remarks: 'Payroll deduction'
    } as any)
    if (added) return added

    // The insert was refused. The usual cause is that a line for this payslip
    // already exists and we had not loaded it — uq_loan_payslip_type is doing its
    // job. Re-read the loan's lines and update in place, so the collection lands
    // once rather than not at all.
    if (!(await refreshLoanLines(loan.uuid))) return undefined
    const found = lineFor(loan.uuid, payslip.uuid, LOAN_ENTRY.Amortization)
    if (!found) return undefined
    if (my.parseMoney(found.amount) == value) return found
    return await putLine({ ...found, amount: value } as LoanTrans)
  }

  return {
    showForm,
    loans,
    loanTrans,
    selectedLoan,
    thisLoan,

    getLoans,
    getLoanTrans,
    saveLoan,
    deleteLoan,

    employeeLoans,
    activeLoans,
    ledgerFor,
    balanceOf,
    refreshBalance,
    syncReleaseLine,
    postAdjustment,
    editAdjustment,
    deleteLine,

    cutIndex,
    cutsInMonth,
    splitAmortization,
    scheduledFor,

    postLoanDeductions
  }
})
